import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BANK_ACCOUNTS, GOZIS_WA_URL, LAYANAN_CARE_TYPES, LAYANAN_DESTINATIONS, formatDate, optionLabel } from "../lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { Ambulance, Loader2, Users, HeartHandshake, ChevronDown, Scissors, MessageCircle, Landmark, FileBarChart2, HandCoins, Gift, QrCode, Scale, Calculator, Beef, Copy, ImagePlus, Trash2, Instagram, Facebook, Youtube, Accessibility, HeartPulse, Baby } from "lucide-react";

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 1 1-2.31-2.83v-3.5a6.37 6.37 0 1 0 5.76 6.33V10.1a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.53z" />
  </svg>
);

const SOCIALS = [
  { icon: MessageCircle, label: "WhatsApp", url: "https://wa.me/628112999157", testid: "social-wa" },
  { icon: Instagram, label: "Instagram", url: "https://www.instagram.com/lazisnu.dawe/", testid: "social-instagram" },
  { icon: Facebook, label: "Facebook", url: "https://www.facebook.com/UPZISLAZISNUKecDaweKudus/", testid: "social-facebook" },
  { icon: Youtube, label: "Youtube", url: "https://www.youtube.com/@nucare_lazisnu_dawe", testid: "social-youtube" },
  { icon: TikTokIcon, label: "TikTok", url: "https://www.tiktok.com/@lazisnu.dawe", testid: "social-tiktok" },
];

const QUICK_MENU = [
  { icon: HandCoins, label: "Zakat", to: "/info/zakat", testid: "menu-zakat" },
  { icon: Gift, label: "Infaq/Sedekah", to: "/info/infaq", testid: "menu-infaq" },
  { icon: QrCode, label: "QRIS Infaq/Sedekah", to: "/qris", testid: "menu-qris" },
  { icon: Scale, label: "Fidyah/Kafarat", to: "/info/fidyah", testid: "menu-fidyah" },
  { icon: Calculator, label: "Kalkulator Zakat", to: "/kalkulator-zakat", testid: "menu-kalkulator" },
  { icon: Beef, label: "Qurban", to: "/info/qurban", testid: "menu-qurban" },
];

function Slideshow() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const fileRef = useRef(null);

  const fetchSlides = useCallback(() => {
    api.get("/public/slideshow").then((r) => {
      setSlides(r.data);
      setIdx(0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchSlides();
  }, [fetchSlides]);

  useEffect(() => {
    if (slides.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 4000);
    return () => clearInterval(t);
  }, [slides.length]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const fd = new FormData();
    fd.append("caption", caption);
    files.forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      await api.post("/slideshow", fd);
      toast.success(`${files.length} foto slideshow ditambahkan`);
      setCaption("");
      fetchSlides();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    const s = slides[idx];
    if (!s?.slide_id) return;
    try {
      await api.delete(`/slideshow/${s.slide_id}`);
      toast.success("Foto slideshow dihapus");
      fetchSlides();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const adminBar = isAdmin && (
    <div className="absolute right-3 top-3 z-10 flex items-center gap-2" data-testid="slideshow-admin-bar">
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid="slideshow-file-input" />
      <input
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        placeholder="Keterangan (opsional)"
        data-testid="slideshow-caption-input"
        className="h-8 w-40 rounded-lg border border-white/30 bg-white/90 px-2 text-xs text-slate-700 backdrop-blur"
      />
      <button
        data-testid="slideshow-add-button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="flex h-8 items-center gap-1.5 rounded-lg bg-emerald-700/95 px-3 text-xs font-semibold text-white backdrop-blur transition-colors duration-200 hover:bg-emerald-600"
      >
        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
        Tambah Foto
      </button>
      {slides[idx]?.slide_id && (
        <button
          data-testid="slideshow-delete-button"
          onClick={handleDelete}
          className="flex h-8 items-center gap-1.5 rounded-lg bg-red-600/95 px-3 text-xs font-semibold text-white backdrop-blur transition-colors duration-200 hover:bg-red-500"
        >
          <Trash2 className="h-3.5 w-3.5" /> Hapus
        </button>
      )}
    </div>
  );

  if (!slides.length) {
    return (
      <div className="relative flex aspect-[21/9] w-full flex-col items-center justify-center gap-3 rounded-xl bg-emerald-950" data-testid="slideshow-empty">
        {adminBar}
        <img src="/logo-lazisnu.png" alt="LAZISNU Dawe" className="h-20 w-20 rounded-xl bg-white object-contain p-2" />
        <p className="text-sm text-emerald-100/70">Dokumentasi pentasyarufan akan tampil di sini</p>
      </div>
    );
  }

  const active = slides[idx];
  return (
    <div className="relative aspect-[21/9] w-full overflow-hidden rounded-xl bg-slate-900" data-testid="slideshow">
      {adminBar}
      {slides.map((s, i) => (
        <img
          key={s.path}
          src={`${API}/files/${s.path}`}
          alt={s.category}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
        />
      ))}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/85 to-transparent px-5 pb-4 pt-14">
        <p className="font-heading text-lg font-bold text-white" data-testid="slideshow-caption">{active.category}</p>
        <p className="text-xs text-slate-300">{active.tx_date ? formatDate(active.tx_date) : ""}{active.tx_date && active.description ? " · " : ""}{active.description || ""}</p>
      </div>
      <div className="absolute bottom-3 right-4 flex gap-1.5">
        {slides.map((s, i) => (
          <button
            key={s.path}
            data-testid={`slideshow-dot-${i}`}
            onClick={() => setIdx(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-5 bg-white" : "w-2 bg-white/50"}`}
          />
        ))}
      </div>
    </div>
  );
}

function RekeningDialog({ open, onOpenChange }) {
  const copyNumber = (num) => {
    navigator.clipboard?.writeText(num.replace(/\s/g, ""));
    toast.success("Nomor rekening disalin");
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="rekening-dialog">
        <DialogHeader>
          <DialogTitle>Rekening Lengkap</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {BANK_ACCOUNTS.map((acc) => (
            <div key={acc.number} className="flex items-center gap-3 rounded-xl border border-slate-200 p-4">
              <div className="flex h-10 w-14 shrink-0 items-center justify-center rounded-md bg-[#00529C] text-xs font-bold text-white">BRI</div>
              <div className="flex-1">
                <p className="text-xs text-slate-500">{acc.label}</p>
                <p className="font-mono text-lg font-bold tracking-wider text-slate-900">{acc.number}</p>
              </div>
              <button
                onClick={() => copyNumber(acc.number)}
                className="rounded-md p-2 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                data-testid={`rekening-copy-${acc.number.replace(/\s/g, "")}`}
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const SOCIAL_PLATFORMS = [
  { key: "instagram", label: "Instagram", handle: "@lazisnu.dawe", url: "https://www.instagram.com/lazisnu.dawe/", icon: Instagram },
  { key: "youtube", label: "Youtube", handle: "@nucare_lazisnu_dawe", url: "https://www.youtube.com/@nucare_lazisnu_dawe", icon: Youtube },
  { key: "tiktok", label: "TikTok", handle: "@lazisnu.dawe", url: "https://www.tiktok.com/@lazisnu.dawe", icon: TikTokIcon },
  { key: "facebook", label: "Facebook", handle: "UPZIS LAZISNU Kec Dawe Kudus", url: "https://web.facebook.com/UPZISLAZISNUKecDaweKudus/", icon: Facebook },
];

const SOCIAL_PENDING = ["instagram", "tiktok", "facebook"];

function SocialMediaSection() {
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    api.get("/public/social-feed").then((r) => setFeed(r.data)).catch(() => setFeed({}));
  }, []);

  if (feed === null) return null;

  const visiblePlatforms = SOCIAL_PLATFORMS.filter((p) => !SOCIAL_PENDING.includes(p.key));

  return (
    <div data-testid="social-section">
      <h2 className="font-heading text-xl font-bold text-slate-800 mb-3">Unggahan Terbaru Media Sosial</h2>
      <div className={`grid grid-cols-1 gap-4 ${visiblePlatforms.length > 1 ? "md:grid-cols-2" : ""}`}>
        {visiblePlatforms.map((p) => {
          const posts = (feed?.[p.key] || []).filter((x) => x.thumbnail_url).slice(0, 6);
          return (
            <Card key={p.key} data-testid={`social-card-${p.key}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-800">
                    <p.icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{p.label}</p>
                    <p className="text-xs text-slate-400 truncate">{p.handle}</p>
                  </div>
                  <a href={p.url} target="_blank" rel="noreferrer" data-testid={`social-link-${p.key}`} className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 hover:underline">
                    Kunjungi
                  </a>
                </div>
              </CardHeader>
              <CardContent>
                {posts.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid={`social-thumbs-${p.key}`}>
                    {posts.map((post, i) => (
                      <a
                        key={post.id || i}
                        href={post.permalink || p.url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`social-thumb-${p.key}-${i}`}
                        className="group relative block aspect-square overflow-hidden rounded-lg bg-slate-100"
                      >
                        <img
                          src={post.thumbnail_url}
                          alt={post.title || `${p.label} ${i + 1}`}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      </a>
                    ))}
                  </div>
                ) : (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`social-profile-${p.key}`}
                    className="flex items-center gap-3 rounded-lg border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500 transition-colors duration-200 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800"
                  >
                    <p.icon className="h-5 w-5" />
                    Lihat unggahan terbaru di {p.label} kami
                  </a>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default function PublicDashboard() {
  const [d, setD] = useState(null);
  const [rekeningOpen, setRekeningOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/public/summary").then((r) => setD(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-dashboard">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3">
          <Link to="/" className="flex items-center gap-3" data-testid="public-logo-link">
            <img src="/logo-lazisnu.png" alt="Logo LAZISNU Dawe" className="h-10 w-10 rounded-lg bg-white object-contain p-0.5" />
            <div className="flex-1">
              <p className="font-heading font-bold text-slate-900 leading-tight">LAZISNU Dawe</p>
              <p className="text-xs text-slate-500">Mari Bantu Sesama</p>
            </div>
          </Link>
          <div className="flex-1" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="layanan-menu-button">
                Layanan <ChevronDown className="ml-1.5 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem data-testid="layanan-menu-ambulance" onClick={() => navigate("/layanan-ambulance")}>
                <Ambulance className="mr-2 h-4 w-4 text-emerald-800" /> Ambulance / MLU
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="layanan-menu-khitan" onClick={() => navigate("/khitan")}>
                <Scissors className="mr-2 h-4 w-4 text-emerald-800" /> Khitan Sehat Gratis
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="layanan-menu-pinjam-alat" onClick={() => navigate("/pinjam-alat")}>
                <Accessibility className="mr-2 h-4 w-4 text-emerald-800" /> Pinjam Alat Kesehatan
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="layanan-menu-kartu-sehat" onClick={() => navigate("/kartu-sehat")}>
                <HeartPulse className="mr-2 h-4 w-4 text-emerald-800" /> Kartu Sehat Gratis
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="layanan-menu-usg" onClick={() => navigate("/usg")}>
                <Baby className="mr-2 h-4 w-4 text-emerald-800" /> Ibu Hamil & USG Gratis
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="layanan-menu-gozis" onClick={() => window.open(GOZIS_WA_URL, "_blank")}>
                <MessageCircle className="mr-2 h-4 w-4 text-emerald-800" /> Go-ZIS (Jemput ZIS)
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="layanan-menu-rekening" onClick={() => setRekeningOpen(true)}>
                <Landmark className="mr-2 h-4 w-4 text-emerald-800" /> Rekening Lengkap
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="layanan-menu-annual" onClick={() => navigate("/annual-report")}>
                <FileBarChart2 className="mr-2 h-4 w-4 text-emerald-800" /> Annual Report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/login">
            <Button data-testid="public-login-button" className="bg-emerald-800 hover:bg-emerald-700 text-white">
              Masuk
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 space-y-6">
        {!d ? (
          <div className="flex justify-center py-24" data-testid="public-loading">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-800" />
          </div>
        ) : (
          <>
            <Slideshow />

            <div className="grid grid-cols-1 gap-4">
              <Card data-testid="public-layanan">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Ambulance className="h-5 w-5 text-emerald-800" />
                    Jadwal Layanan Mendatang
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(d.layanan?.mendatang || []).length === 0 ? (
                    <p className="py-8 text-center text-sm text-slate-400">Belum ada jadwal layanan</p>
                  ) : (
                    <div className="space-y-2">
                      {d.layanan.mendatang.map((r, i) => (
                        <div key={i} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
                          <p className="font-medium text-slate-800">{r.day}, {formatDate(r.date)}{r.time ? ` · ${r.time}` : ""}</p>
                          <p className="text-xs text-slate-500">
                            {r.desa ? `Desa ${r.desa} · ` : ""}{optionLabel(LAYANAN_DESTINATIONS, r.destination)} · {optionLabel(LAYANAN_CARE_TYPES, r.care_type)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card data-testid="public-donatur">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="h-5 w-5 text-emerald-800" />
                    Jumlah Donatur per Jenis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {(d.donatur || []).map((x) => (
                      <div key={x.key} className="rounded-lg border border-slate-200 px-3 py-2.5 text-center" data-testid={`public-donatur-${x.key}`}>
                        <p className="text-2xl font-bold font-heading text-emerald-800">{x.jumlah}</p>
                        <p className="text-xs text-slate-500">{x.label}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="public-penerima-manfaat">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <HeartHandshake className="h-5 w-5 text-emerald-800" />
                    Jumlah Penerima Manfaat per Program
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(d.penerima_manfaat || []).length === 0 ? (
                    <p className="py-6 text-center text-sm text-slate-400">Belum ada data penerima manfaat</p>
                  ) : (
                    <div className="space-y-2 max-h-72 overflow-auto">
                      {d.penerima_manfaat.map((x) => (
                        <div key={x.label} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
                          <span className="text-slate-700">{x.label}</span>
                          <span className="font-bold font-heading text-emerald-800">{x.jumlah}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <SocialMediaSection />

            <div>
              <h2 className="font-heading text-xl font-bold text-slate-800 mb-3">Layanan ZIS & Keumatan</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="quick-menu">
                {QUICK_MENU.map((m) => (
                  <button
                    key={m.testid}
                    data-testid={m.testid}
                    onClick={() => navigate(m.to)}
                    className="flex flex-col items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-5 text-center transition-all duration-200 hover:-translate-y-[2px] hover:border-emerald-300 hover:shadow-md"
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-100 text-emerald-800">
                      <m.icon className="h-5 w-5" strokeWidth={1.8} />
                    </div>
                    <span className="text-xs font-semibold text-slate-700 leading-tight">{m.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <RekeningDialog open={rekeningOpen} onOpenChange={setRekeningOpen} />
          </>
        )}
      </main>

      <footer className="border-t border-slate-200 bg-white py-6" data-testid="public-footer">
        <div className="flex flex-col items-center gap-3">
          <p className="text-sm font-semibold text-slate-700">Hubungi Kami :</p>
          <div className="flex items-center gap-3">
            {SOCIALS.map((s) => (
              <a
                key={s.testid}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                aria-label={s.label}
                data-testid={s.testid}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-all duration-200 hover:-translate-y-[2px] hover:border-emerald-600 hover:bg-emerald-800 hover:text-white"
              >
                <s.icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
